import { useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE_URL } from '../constants';
import { Method, Header, QueryParams } from './types';

const getHeaders = (headers?: Header, token?: string): Header => ({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(headers ?? {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
});

const handleResponse = (response: Response) => response.ok
    ? response.json()
    : response.json().then(error => Promise.reject(error));

export const toQueryParams = (query: QueryParams) => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) {
            params.append(key, value);
        }
    });
    return params.size ? `?${params.toString()}` : '';
}

export const useService = () => {
    const sendFetch = (url: string, method: Method, headers: Header, body?: string) => window.fetch(`${API_BASE_URL}${url}`, { method, headers, body });
    const auth0 = useAuth0();
    const withAuthorization = useCallback(async (url: string, method: Method, headers?: Header, body?: string) => {
        const token = await auth0.getAccessTokenSilently();
        return sendFetch(url, method, getHeaders(headers, token), body);
    }, [auth0]);

    const sendGetWithAuthorization = useCallback((url: string, headers?: Header) => withAuthorization(url, Method.GET, headers), [withAuthorization]);
    const sendGetWithoutAuthorization = useCallback((url: string, headers?: Header) => sendFetch(url,  Method.GET, getHeaders(headers)), []);

    const sendPostWithAuthorization = useCallback((url: string, body: unknown, headers?: Header) => withAuthorization(url, Method.POST, headers, JSON.stringify(body)), [withAuthorization]);
    const sendPostWithoutAuthorization = useCallback((url: string, body: unknown, headers?: Header) => sendFetch(url,  Method.POST, getHeaders(headers), JSON.stringify(body)), []);

    const sendPutWithAuthorization = useCallback((url: string, body: unknown, headers?: Header) => withAuthorization(url, Method.PUT, headers, JSON.stringify(body)), [withAuthorization]);
    const sendPutWithoutAuthorization = useCallback((url: string, body: unknown, headers?: Header) => sendFetch(url, Method.PUT, getHeaders(headers), JSON.stringify(body)),  []);

    const sendDeleteWithAuthorization = useCallback((url: string, headers?: Header) => withAuthorization(url, Method.DELETE, headers), [withAuthorization]);
    const sendDeleteWithoutAuthorization = useCallback((url: string, headers?: Header) => sendFetch(url, Method.DELETE, getHeaders(headers)), []);

    return useMemo(() => ({
        sendGetWithAuthorization,
        sendGetWithoutAuthorization,
        sendPostWithAuthorization,
        sendPostWithoutAuthorization,
        sendPutWithAuthorization,
        sendPutWithoutAuthorization,
        sendDeleteWithAuthorization,
        sendDeleteWithoutAuthorization,
        handleResponse
    }), [
        sendGetWithAuthorization,
        sendGetWithoutAuthorization,
        sendPostWithAuthorization,
        sendPostWithoutAuthorization,
        sendPutWithAuthorization,
        sendPutWithoutAuthorization,
        sendDeleteWithAuthorization,
        sendDeleteWithoutAuthorization,
        handleResponse
    ]);
};